import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useEscapeKey } from '@spark';
import { useWalkthrough } from '../useWalkthrough';
import {
    CREATE_MODEL_WALKTHROUGH_ID,
    WALKTHROUGH_STATUS,
    getWalkthrough,
    isCenteredStep,
    isCountedStep,
    stepSelector,
    walkthroughStyle,
} from '../registry';
import { applicableSteps, resolvePointer } from '../utils/resolvePointer';
import { useWalkthroughTarget } from '../hooks/useWalkthroughTarget';
import { useDemoActivation } from '../hooks/useDemoActivation';
import CoachMark from './CoachMark';

/** User activity on the held step's anchor that releases the hold. */
const RELEASE_EVENTS = ['pointerdown', 'keydown', 'drop', 'change'];

/** When a runner starts its walkthrough on its own. */
export const WALKTHROUGH_AUTO_START = Object.freeze({
    /** Only resume a walkthrough persisted as in progress (reload, re-entry). */
    resumeOnly: 'resume-only',
    /** Resume as above, and also start the first time this user meets it (no saved state at all). */
    neverSeen: 'never-seen',
    /** Never on its own: the host calls `start()` (the demo tour). */
    never: 'never',
});

/**
 * Drives one walkthrough on the page that hosts it. It reads the pointer and
 * `ctx` from the provider, moves the pointer with `resolvePointer`
 * (auto-completing satisfied steps, deferring inapplicable ones, skipping
 * steps whose page was left), and renders the current step's coach mark once
 * its anchor exists on the current wizard page.
 *
 * Nothing is resolved before the host has published the slices the definition
 * lists in `requiredContext`; a reopened draft's form arrives asynchronously,
 * and predicates run against the empty form in the meantime would move the
 * pointer off the step the tour was left at.
 *
 * The only state it owns is the *hold*: the step the pointer was on when this
 * runner became ready (a reload or a re-entry into the wizard) stays put until
 * the user touches its anchor, even if its completion predicate is already
 * satisfied. Drafts restore defaults the user never chose (a fallback label,
 * the single-label default), and without the hold those would push the tour
 * past the step it was left at.
 *
 * Chrome follows the definition's `style`: a `tour` is moved by acting on the
 * highlighted control (or `Skip this step`); a `guide` also offers `Next`
 * (move on without acting), `Skip` and an `X` that closes the whole guide.
 * Reaching the end of the steps ends the walkthrough as completed and calls
 * `onFinished`.
 *
 * Props:
 * - `walkthroughId`: which definition to run.
 * - `currentPage`: the wizard page currently rendered (e.g. `business-goals`).
 * - `pageOrder`: ordered page keys of this wizard variant; steps whose page is
 *   missing are skipped. Memoise it in the host.
 * - `autoStart`: see `WALKTHROUGH_AUTO_START`.
 * - `completeOnLeave`: end the walkthrough as completed when this runner
 *   unmounts while it is still active (the task tour ends with its page).
 * - `onFill(step)`: demo mode. Activating the highlighted anchor (a click or
 *   a key press on it), `Skip` or `Next` first runs `onFill` (which
 *   auto-fills the step's content) and then completes the step; the anchor
 *   itself never receives the activation. Steps with `activation: 'option'`
 *   complete when the hinted option is clicked instead, and those with
 *   `activation: 'passthrough'` through their predicate (see
 *   `hooks/useDemoActivation.js`).
 * - `onAwaitingPage({ from, to })`: called once when the pointer moved to a
 *   step on a later page than the one rendered — every mark of `from` is
 *   done and the host has to turn the page (the demo does so by itself).
 * - `onFinished(walkthroughId)`: called once after the last step completed.
 */
export default function WalkthroughRunner({
    walkthroughId = CREATE_MODEL_WALKTHROUGH_ID,
    currentPage,
    pageOrder,
    autoStart = WALKTHROUGH_AUTO_START.resumeOnly,
    completeOnLeave = false,
    onFill,
    onAwaitingPage,
    onFinished,
}) {
    const {
        active,
        states,
        seeded,
        stepIndex,
        skippedIds,
        completedIds,
        deferredIds,
        ctx,
        start,
        advance,
        completeStep,
        skipStep,
        end,
        setVisibleStep,
    } = useWalkthrough();
    const definition = getWalkthrough(walkthroughId);
    const steps = definition?.steps || [];
    const variant = walkthroughStyle(definition);
    const saved = states[walkthroughId];
    const savedStatus = saved?.status;
    const isActive = active === walkthroughId;
    const requiredContext = definition?.requiredContext || [];
    const ready = isActive && requiredContext.every((key) => ctx?.[key] !== undefined);

    // Auto-start: resume one persisted as in-progress (reload, re-entry) and,
    // for `never-seen`, begin one this user has no record of yet. Only once
    // the bootstrap preferences are in, and never over another active one.
    useEffect(() => {
        if (!definition || active || autoStart === WALKTHROUGH_AUTO_START.never) return;
        if (savedStatus === WALKTHROUGH_STATUS.inProgress) {
            start(walkthroughId);
            return;
        }
        if (autoStart === WALKTHROUGH_AUTO_START.neverSeen && seeded && saved === undefined) {
            start(walkthroughId, { restart: true });
        }
    }, [definition, active, autoStart, savedStatus, saved, seeded, start, walkthroughId]);

    // `null` until the tour is ready here, then `{ index }`: the step held at
    // that moment, or -1 once the user acted on it. Recorded once per mount.
    const [hold, setHold] = useState(null);
    useEffect(() => {
        if (ready && hold === null) setHold({ index: stepIndex });
    }, [ready, hold, stepIndex]);
    const holdIndex = !ready ? -1 : hold === null || hold.index === stepIndex ? stepIndex : -1;

    const resolved = useMemo(
        () =>
            ready
                ? resolvePointer({
                      steps,
                      stepIndex,
                      ctx,
                      currentPage,
                      pageOrder,
                      deferredIds,
                      skippedIds,
                      completedIds,
                      holdIndex,
                  })
                : null,
        [ready, steps, stepIndex, ctx, currentPage, pageOrder, deferredIds, skippedIds, completedIds, holdIndex]
    );

    // The reducer ignores a resolution that changes nothing.
    useEffect(() => {
        if (resolved) advance(resolved);
    }, [resolved, advance]);

    // Past the last step: the walkthrough is done.
    const onFinishedRef = useRef(onFinished);
    onFinishedRef.current = onFinished;
    const finished = ready && resolved !== null && resolved.stepIndex >= steps.length && steps.length > 0;
    useEffect(() => {
        if (!finished) return;
        end(WALKTHROUGH_STATUS.completed, walkthroughId);
        onFinishedRef.current?.(walkthroughId);
    }, [finished, end, walkthroughId]);

    // The pointer is on a later page: this page's marks are done.
    const onAwaitingPageRef = useRef(onAwaitingPage);
    onAwaitingPageRef.current = onAwaitingPage;
    const pointerStep = resolved && resolved.stepIndex < steps.length ? steps[resolved.stepIndex] : null;
    const awaitingPage =
        pointerStep &&
        pointerStep.wizardStep !== currentPage &&
        pageOrder &&
        pageOrder.indexOf(pointerStep.wizardStep) > pageOrder.indexOf(currentPage)
            ? pointerStep.wizardStep
            : null;
    useEffect(() => {
        if (!awaitingPage) return;
        onAwaitingPageRef.current?.({ from: currentPage, to: awaitingPage });
    }, [awaitingPage, currentPage]);

    // The task tour ends with the page that hosts it.
    const activeRef = useRef(false);
    activeRef.current = isActive;
    useEffect(() => {
        if (!completeOnLeave) return undefined;
        return () => {
            if (activeRef.current) end(WALKTHROUGH_STATUS.completed, walkthroughId);
        };
    }, [completeOnLeave, end, walkthroughId]);

    const step = resolved && resolved.stepIndex === stepIndex ? steps[stepIndex] : null;
    const visibleStep = step && step.wizardStep === currentPage ? step : null;
    const centered = Boolean(visibleStep && isCenteredStep(visibleStep));
    const anchored = Boolean(visibleStep) && !centered;

    const { element, rect } = useWalkthroughTarget(anchored ? stepSelector(visibleStep) : null, {
        enabled: anchored,
        anchorRect: visibleStep?.anchorRect,
        observe: visibleStep?.observe,
        // In demo mode the anchor's activation is intercepted below instead.
        completeOnEvents: onFill ? undefined : visibleStep?.completeOnEvents,
        onComplete: completeStep,
    });
    const shown = centered || Boolean(visibleStep && rect);

    // The dashed hint on the one option / card the bubble asks for. It lives
    // in a portaled panel more often than not, so it is looked up in the
    // document and only once the mark is up (the host opens the panel then).
    const hint = shown ? visibleStep.hint : undefined;
    const { element: hintElement, rect: hintRect } = useWalkthroughTarget(hint?.selector || null, {
        enabled: Boolean(hint),
        scrollIntoView: false,
    });

    // Lets the host page react to the mark being on screen (auto-open a menu,
    // switch a section on) through `useWalkthroughStepActive` & co. Published
    // only once the anchor is measured so a menu the host opens in response
    // registers its Escape handler *after* this runner's and takes the first
    // Esc; the second one dismisses the tour.
    const shownStepId = shown ? visibleStep.id : null;
    useEffect(() => {
        setVisibleStep(shownStepId);
    }, [shownStepId, setVisibleStep]);
    useEffect(() => () => setVisibleStep(null), [setVisibleStep]);

    // Any interaction with the held step's anchor hands control back to the
    // completion predicates. An option step's panel is portaled and may be
    // auto-opened for a resumed step, so picking in it counts as well — the
    // trigger itself is never touched then.
    const observed = visibleStep?.observe;
    useEffect(() => {
        if (!element || holdIndex === -1) return undefined;
        const panels = observed || [];
        const within = (target) => {
            if (!(target instanceof Node)) return false;
            if (element.contains(target)) return true;
            const el = target instanceof Element ? target : target.parentElement;
            return Boolean(el) && panels.some((selector) => el.closest(selector));
        };
        const release = (event) => {
            if (within(event.target)) setHold({ index: -1 });
        };
        RELEASE_EVENTS.forEach((type) => document.addEventListener(type, release, true));
        return () => RELEASE_EVENTS.forEach((type) => document.removeEventListener(type, release, true));
    }, [element, holdIndex, observed]);

    // Demo: fill the step's content on behalf of the user, then move on.
    const onFillRef = useRef(onFill);
    onFillRef.current = onFill;
    const fillAndComplete = useCallback(() => {
        if (visibleStep) onFillRef.current?.(visibleStep);
        completeStep();
    }, [visibleStep, completeStep]);
    useDemoActivation({
        enabled: Boolean(onFill),
        element,
        step: visibleStep,
        hintElement,
        onActivate: fillAndComplete,
        // The host's handler picked the option; nothing left to fill.
        onOptionPicked: completeStep,
    });

    const dismiss = useCallback(() => end(WALKTHROUGH_STATUS.dismissed, walkthroughId), [end, walkthroughId]);
    useEscapeKey(dismiss, shown);

    const counted = useMemo(
        () => applicableSteps({ steps, ctx, pageOrder }).filter(isCountedStep),
        [steps, ctx, pageOrder]
    );

    if (!shown) return null;

    const position = counted.indexOf(visibleStep);
    const stepNumber = position === -1 ? null : position + 1;
    const stepTotal = stepNumber === null ? null : Math.max(stepNumber, counted.length);
    const isLast = counted.length > 0 && counted[counted.length - 1] === visibleStep;
    const skip = onFill ? fillAndComplete : skipStep;
    const next = onFill ? fillAndComplete : completeStep;

    return (
        <CoachMark
            variant={variant}
            title={visibleStep.title}
            body={visibleStep.body}
            placement={visibleStep.placement}
            align={visibleStep.align}
            rect={rect}
            hintRect={hintRect}
            hintOffset={hint?.offset}
            centered={centered}
            stepNumber={stepNumber}
            stepTotal={stepTotal}
            isLast={isLast}
            nextLabel={visibleStep.cta}
            onSkip={skip}
            onNext={next}
            onClose={dismiss}
            targetElement={element}
        />
    );
}
